import { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { sendPasswordResetEmail } from 'firebase/auth';
import { ApiError } from '../lib/api';
import { auth } from '../lib/firebase';
import { useSession } from '../lib/session';
import { Alert, Card, Field } from '../components/ui';

export default function Login() {
  const { signIn } = useSession();
  const navigate = useNavigate();
  const location = useLocation();
  const from = (location.state as { from?: string } | null)?.from ?? '/';

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<ApiError | null>(null);
  const [busy, setBusy] = useState(false);
  const [resetSent, setResetSent] = useState<string | null>(null);

  async function handleSubmit(event: React.FormEvent) {
    event.preventDefault();
    setError(null);
    setBusy(true);
    try {
      await signIn(email, password);
      navigate(from, { replace: true });
    } catch (err) {
      setError(err instanceof ApiError ? err : new ApiError(0, 'error', 'Could not sign in.'));
    } finally {
      setBusy(false);
    }
  }

  async function handleForgot() {
    if (!email) {
      setError(new ApiError(400, 'bad_request', 'Enter your email address first.'));
      return;
    }
    setError(null);
    setBusy(true);
    try {
      // Firebase answers the same way whether or not the address has an
      // account — the enumeration-safe behaviour the old hand-built reset
      // endpoint had to implement by hand comes for free here.
      await sendPasswordResetEmail(auth, email, {
        url: `${window.location.origin}/reset`,
        handleCodeInApp: true,
      });
      setResetSent('If that email has an account, a reset link is on its way.');
    } catch (err) {
      setError(err instanceof ApiError ? err : new ApiError(0, 'error', 'Could not send a reset link.'));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="mx-auto flex min-h-screen max-w-md flex-col justify-center px-4 py-8">
      <h1 className="mb-1 text-center text-2xl font-bold">
        <span aria-hidden="true">🔥</span> FIRE Tracker
      </h1>
      <p className="mb-6 text-center text-sm text-ink-muted dark:text-ink-inverse-muted">
        Track your portfolio, plan your SIPs, model your independence.
      </p>

      <Card title="Sign in">
        <form onSubmit={handleSubmit} className="space-y-4" noValidate>
          {error && <Alert tone="critical">{error.message}</Alert>}
          {resetSent && <Alert tone="info">{resetSent}</Alert>}

          <Field label="Email" error={error?.fieldError('email')}>
            {(props) => (
              <input
                {...props}
                className="input"
                type="email"
                name="email"
                autoComplete="username"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            )}
          </Field>

          <Field label="Password" error={error?.fieldError('password')}>
            {(props) => (
              <input
                {...props}
                className="input"
                type="password"
                name="password"
                autoComplete="current-password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
            )}
          </Field>

          <button type="submit" className="btn-primary w-full" disabled={busy}>
            {busy ? 'Signing in…' : 'Sign in'}
          </button>

          <div className="flex items-center justify-between text-sm">
            <button
              type="button"
              className="underline text-ink-muted dark:text-ink-inverse-muted"
              onClick={handleForgot}
              disabled={busy}
            >
              Forgot password?
            </button>
            <Link to="/register" className="underline">
              Create an account
            </Link>
          </div>
        </form>
      </Card>
    </div>
  );
}
